/* global axios */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import api from '../services/apiService';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Select from 'react-select';


// Funktion zur Formatierung von Datum+Uhrzeit in "TT.MM.JJJJ HH:MM"
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const TriState = ({ value, onChange, labels = ['—', 'Ja', 'Nein'] }) => {
  const next = () => {
    if (value === null) return onChange(true);
    if (value === true) return onChange(false);
    return onChange(null);
  };
  const bg = value === true ? '#28a745' : value === false ? '#dc3545' : '#6c757d';
  return (
    <button
      type="button"
      onClick={next}
      style={{ width: 80, minHeight: 34, padding: '4px 8px', borderRadius: 6, border: 'none', color: '#fff', background: bg, cursor: 'pointer' }}
      title="Klicken zum Umschalten (— → Ja → Nein)"
    >
      {value === null ? labels[0] : value ? labels[1] : labels[2]}
    </button>
  );
};

const UVVInspectionForm = ({ mode: propMode }) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // akzeptiert :inspectionId | :id | state.inspectionId
  const paramId  = params.inspectionId || params.id;
  const stateId  = location.state?.inspectionId;
  const inspectionId = paramId || stateId || null;

  // Wenn ID vorhanden → immer Edit-Modus (verhindert hängenden Loader)
  const mode = inspectionId ? 'edit' : (propMode || 'create');
  const isCreate = mode === 'create';

  // Kopf
  const [header, setHeader] = useState(null);
  const [savingHead, setSavingHead] = useState(false);
  const [headerDirty, setHeaderDirty] = useState(false);

  // Items
  const [items, setItems] = useState([]);
  const [itemsDirty, setItemsDirty] = useState(false);
  const [savingItems, setSavingItems] = useState(false);

  // UI Filter/Suche
  const [only, setOnly] = useState('all'); // all | open | notok
  const [q, setQ] = useState('');

  // CREATE-Felder (RevenueForm-ähnliche Filterleiste)
  const [createMachineId, setCreateMachineId] = useState('');
  const [createUvvType, setCreateUvvType]     = useState('main');
  const [createDate, setCreateDate]           = useState(() => new Date().toISOString().slice(0, 10));
  const [createLoading, setCreateLoading]     = useState(false);
  const [machineOptions, setMachineOptions]   = useState([]);

  // Prüfer-Optionen
  const [inspectorOptions, setInspectorOptions] = useState([]);
  const selectedInspector = useMemo(
    () => inspectorOptions.find(o => String(o.value) === String(header?.inspectorId)) ?? null,
    [inspectorOptions, header?.inspectorId]
  );

  // CREATE-Felder
  const [createCustomerId, setCreateCustomerId] = useState('');

  // Optionen
  const [customerOptions, setCustomerOptions] = useState([]);
  const createDisabled = !createMachineId || !createCustomerId || createLoading;

  // Kontakte/Besteller
  const [contactOptions,  setContactOptions]  = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]); // rohe Kontakte
  const [contactsLoadedForCustomer, setContactsLoadedForCustomer] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  // 🔒 Refs zur Entkoppelung (verhindert useCallback-Dependency-Loops)
  const contactsLoadedForCustomerRef = useRef(null);
  const customerContactsRef = useRef([]);

  useEffect(() => { customerContactsRef.current = customerContacts; }, [customerContacts]);
  useEffect(() => { contactsLoadedForCustomerRef.current = contactsLoadedForCustomer; }, [contactsLoadedForCustomer]);

  // react-select styles
  const selectStyles = {
    control:   (base) => ({ ...base, minHeight: 34, width: '100%', marginBottom: 16 }),
    container: (base) => ({ ...base, marginTop: -5 }),
    menuPortal:(base) => ({ ...base, zIndex: 9999 }),
    menu:      (base) => ({ ...base, zIndex: 9999 }),
  };

  // Maschinenoptionen für Create
  useEffect(() => {
    if (!isCreate || inspectionId) return;
    (async () => {
      try {
        const { data } = await api.get('/uvv/inspections/filter-options');
        const opts = (data?.machines || []).map(m => ({
          value: m.id,
          label: m.serialNumber ? `${m.name} (${m.serialNumber})` : m.name,
        }));
        setMachineOptions(opts);
      } catch (e) {
        console.error('Load filter-options failed', e);
      }
    })();
  }, [isCreate, inspectionId]);

  // Kunden (Create)
  useEffect(() => {
    if (!isCreate || inspectionId) return;
    (async () => {
      try {
        const { data } = await api.get('/customers', { params: { pageSize: 50 } });
        const list = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
        const opts = list.map(c => ({
          value: c.id,
          label: c.company || c.name || `#${c.id}`,
        }));
        setCustomerOptions(opts);
      } catch (e) {
        console.error('Kunden laden fehlgeschlagen', e);
      }
    })();
  }, [isCreate, inspectionId]);

  // Kunden (allgemein – z.B. für Edit)
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const { data } = await api.get('/customers');
        setCustomerOptions((data?.rows || data || []).map(c => ({
          value: c.id,
          label: c.company || [c.firstName, c.lastName].filter(Boolean).join(' ') || `#${c.id}`,
        })));
      } catch (e) {
        console.error('Kunden laden fehlgeschlagen:', e);
      }
    };
    loadCustomers();
  }, []);

  // Kontakte für Kunde laden – STABIL, keine State-Dependencies
  const loadContactsForCustomer = useCallback(async (customerId, contactId = null) => {
    try {
      if (!customerId) {
        setCustomerContacts([]);
        setContactOptions([]);
        setSelectedContact(null);
        setContactsLoadedForCustomer(null);
        contactsLoadedForCustomerRef.current = null;
        return;
      }

      const alreadyFor = contactsLoadedForCustomerRef.current;
      const force = !!contactId;

      if (!force && alreadyFor === customerId) {
        if (contactId) {
          const selected = customerContactsRef.current.find(c => String(c.id) === String(contactId));
          if (selected) {
            const next = {
              value: selected.id,
              label: [selected.contactFirstName, selected.contactLastName].filter(Boolean).join(' ') || `#${selected.id}`,
            };
            setSelectedContact(prev => (prev?.value === next.value ? prev : next));
          }
        }
        return;
      }

      console.log(`Lade Kontakte für Kunde #${customerId} (nur Besteller)…`);
      const { data } = await api.get(`/contacts/customer/${customerId}`, { params: { contactFunction: 'Besteller' } });

      const besteller = (data?.rows || data || []).filter(c => {
        const v = (c.contactFunction || c.crmRole || '').toString().toLowerCase();
        return v === 'besteller';
      });

      setCustomerContacts(besteller);

      const opts = besteller.map(c => ({
        value: c.id,
        label: [c.contactFirstName, c.contactLastName].filter(Boolean).join(' ') || `#${c.id}`,
        email: c.contactEmail,
        phone: c.contactPhone,
      }));
      setContactOptions(opts);

      setContactsLoadedForCustomer(customerId);
      contactsLoadedForCustomerRef.current = customerId;

      if (contactId) {
        const match = opts.find(o => String(o.value) === String(contactId));
        const next = match || null;
        setSelectedContact(prev => (prev?.value === next?.value ? prev : next));
        if (!match) {
          console.warn(`Vorbelegung: Kontakt #${contactId} nicht in Bestellerliste.`);
        }
      } else {
        setSelectedContact(prev =>
          prev && opts.every(o => String(o.value) !== String(prev.value)) ? null : prev
        );
      }

      console.log('Besteller geladen:', besteller.length);
    } catch (error) {
      console.error('Fehler beim Laden der Besteller:', error);
      setCustomerContacts([]);
      setContactOptions([]);
      setSelectedContact(null);
      setContactsLoadedForCustomer(null);
      contactsLoadedForCustomerRef.current = null;
    }
  }, []); // 👈 keine deps

  // Kontakte laden bei Änderung von Kunde/Kontakt im Header
  useEffect(() => {
    if (!header) {
      setSelectedContact(null);
      return;
    }
    if (header.customerId == null) {
      setSelectedContact(null);
      setContactOptions([]);
      return;
    }
    loadContactsForCustomer(header.customerId, header.contactId ?? null);
  }, [header?.customerId, header?.contactId, loadContactsForCustomer, header]);

  useEffect(() => {
  if (header && typeof header.inspectionToLW === 'undefined') {
    setHeader(h => ({ ...h, inspectionToLW: false }));
  }
}, [header]);

  // Laden (bei vorhandener ID)
  const load = useCallback(async () => {
    if (!inspectionId) return;
    try {
      const res = await api.get(`/uvv/inspections/${inspectionId}`);
      setHeader(res.data);
      setItems(res.data.inspectionItems || []);
    } catch (e) {
      console.error('Load inspection failed', e);
      alert('Fehler beim Laden der Prüfung.');
    }
  }, [inspectionId]);

  useEffect(() => {
    if (inspectionId) load();
  }, [inspectionId, load]);

  // Prüfer-Optionen laden
  useEffect(() => {
    api.get('/employees', { params: { /* z.B. role: 'inspector', active: true */ } })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        const opts = list.map(e => ({
          value: e.id,
          label: [e.firstName, e.lastName].filter(Boolean).join(' ') || `#${e.id}`,
        }));
        setInspectorOptions(opts);
      })
      .catch(err => console.error('Inspectors laden fehlgeschlagen:', err));
  }, []);

  // Progress-Balkenwerte
  const stats = useMemo(() => {
    const total = items.length;
    let done = 0, failed = 0, pending = 0;
    for (const it of items) {
      const hasNull = it.completeness === null || it.conditionOk === null;
      const hasFail = it.completeness === false || it.conditionOk === false;
      if (hasFail) failed += 1;
      if (hasNull) pending += 1;
      if (!hasFail && !hasNull) done += 1;
    }
    return { total, done, pending, failed, donePct: total ? (done / total) * 100 : 0 };
  }, [items]);

  // Filterung der Items
  const filtered = useMemo(() => {
    let arr = items;
    if (only === 'open') {
      arr = arr.filter(it => it.completeness === null || it.conditionOk === null);
    } else if (only === 'notok') {
      arr = arr.filter(it => it.completeness === false || it.conditionOk === false);
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter(
        it =>
          (it.category || '').toLowerCase().includes(s) ||
          (it.sectionNumber || '').toLowerCase().includes(s) ||
          (it.description || '').toLowerCase().includes(s) ||
          (it.remarks || '').toLowerCase().includes(s)
      );
    }
    return arr;
  }, [items, only, q]);

  // Header speichern
  const saveHeader = async () => {
    if (!inspectionId) return;
    try {
      setSavingHead(true);
      const payload = {
        inspectorId: header.inspectorId ?? null,
        inspectionDate: header.inspectionDate ?? null,
        uvvType: header.uvvType ?? 'main',
        usageHours: header.usageHours ?? null,
        defects: header.defects ?? null,
        reportNote: header.reportNote ?? null,
        note: header.note ?? null,
        result: header.result || 'pending',
        // erweitert:
        customerId: header.customerId ?? null,
        contactId: header.contactId ?? null,
        operationLocation: header.operationLocation ?? null,
        inspectionToLW: !!header.inspectionToLW,
      };
      await api.put(`/uvv/inspections/${inspectionId}`, payload);
      await load();
      setHeaderDirty(false);
      alert('Kopf gespeichert.');
    } catch (e) {
      console.error('Save head failed', e);
      alert('Fehler beim Speichern des Kopfs.');
    } finally {
      setSavingHead(false);
    }
  };

  // Items speichern (Bulk)
  const saveItems = async () => {
    if (!inspectionId) return;
    try {
      setSavingItems(true);
      const payload = {
        items: items.map(it => ({
          id: it.id,
          completeness: it.completeness,
          conditionOk: it.conditionOk,
          remarks: it.remarks || null,
        })),
      };
      await api.patch(`/uvv/inspections/${inspectionId}/items`, payload);
      await load();
      setItemsDirty(false);
      alert('Positionen gespeichert.');
    } catch (e) {
      console.error('Save items failed', e);
      alert('Fehler beim Speichern der Positionen.');
    } finally {
      setSavingItems(false);
    }
  };

  const updateItemField = (rowId, field, value) => {
    setItems(prev => prev.map(it => (it.id === rowId ? { ...it, [field]: value } : it)));
    setItemsDirty(true);
  };

  // ---------- CREATE-Ansicht ----------
  if (isCreate && !inspectionId) {
    const selectedMachine =
      machineOptions.find(o => String(o.value) === String(createMachineId)) || null;

    return (
      <div style={{ maxWidth: 1100, margin: 'auto' }}>
        <Helmet>
          <title>UVV: Neue Prüfung</title>
          <meta name="description" content="UVV-Prüfung anlegen" />
        </Helmet>

        <h2>🧪 Neue UVV-Prüfung</h2>

        <form style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            {/* Maschine */}
            <div style={{ flex: '1 1 200px', minWidth: 200 }}>
              <label htmlFor="machineSelect" style={{ display: 'inline-block', marginBottom: 4 }}></label>
              <Select
                inputId="machineSelect"
                isClearable
                placeholder="Maschine wählen…"
                options={machineOptions}
                value={selectedMachine}
                onChange={(opt) => setCreateMachineId(opt ? String(opt.value) : '')}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPlacement="auto"
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>

            {/* Datum */}
            <div style={{ flex: '1 1 160px', minWidth: 160 }}>
              <label>Prüfdatum</label>
              <input
                type="date"
                value={createDate}
                onChange={(e) => setCreateDate(e.target.value)}
                style={{ width: '100%', minHeight: 34 }}
              />
            </div>

            {/* Art */}
            <div style={{ flex: '1 1 160px', minWidth: 160, marginLeft: '-15px' }}>
              <label></label>
              <select
                value={createUvvType}
                onChange={(e) => setCreateUvvType(e.target.value)}
                style={{ width: '100%', minHeight: 34 }}
              >
                <option value="main">Hauptprüfung</option>
                <option value="retest">Nachprüfung</option>
              </select>
            </div>

            {/* Aktionen rechts (Zeile 1) */}
            <div style={{ marginLeft: '20px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: '13px' }}>
              <button
                type="button"
                disabled={!createMachineId || !createCustomerId || createLoading}
                onClick={async () => {
                  try {
                    setCreateLoading(true);
                    const payload = {
                      machineId: Number(createMachineId),
                      customerId: Number(createCustomerId),
                      uvvType: createUvvType,
                      inspectionDate: createDate || null,
                    };
                    const { data } = await api.post('/uvv/inspections', payload);
                    navigate(`/uvv/inspections/${data.id}/edit`, { replace: true });
                  } catch (e) {
                    console.error('Create draft failed', e);
                    alert(e?.response?.data?.error || 'Erstellen fehlgeschlagen. Bitte Pflichtfelder prüfen.');
                  } finally {
                    setCreateLoading(false);
                  }
                }}
              >
                {createLoading ? 'Erstelle…' : 'anlegen'}
              </button>

              <button type="button" onClick={() => navigate('/uvvInspectionList')}>
                Liste
              </button>
            </div>

            {/* harter Zeilenumbruch */}
            <div style={{ flexBasis: '100%', height: 0 }} />

            {/* Kunde */}
            <div style={{ flex: '1 1 200px', minWidth: 200 }}>
              <label style={{ display: 'inline-block', marginBottom: 4 }}></label>
              <Select
                isClearable
                placeholder="Kunde wählen…"
                options={customerOptions}
                value={customerOptions.find(o => String(o.value) === String(createCustomerId)) || null}
                onChange={(opt) => setCreateCustomerId(opt ? String(opt.value) : '')}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={{
                  control:   (base) => ({ ...base, minHeight: 34, width: '29%', marginBottom: 16 }),
                  container: (base) => ({ ...base, marginTop: -5 }),
                  menuPortal:(base) => ({ ...base, zIndex: 9999 }),
                  menu:      (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          </div>
        </form>
      </div>
    );
  }

  // ---------- EDIT-Ansicht ----------
  if (!header) {
    return (
      <div style={{ maxWidth: 1100, margin: 'auto' }}>
        <p>Lade UVV-Prüfung…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: 'auto' }}>
      <Helmet>
        <title>UVV: {header.certificateNumber || `#${header.id}`}</title>
        <meta name="description" content="UVV-Prüfung bearbeiten" />
      </Helmet>

      <h2>✅ UVV-Prüfung</h2>

      {/* Erstell-/Änder-Info analog zur Werkstattorder */}
<div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }}>
  <div>
    <strong>Erstellt von:</strong>{' '}
    {header.creator
      ? `${header.creator.firstName} ${header.creator.lastName}`
      : 'Unbekannt'}{' '}
    am {formatDateTime(header.createdAt)}
  </div>
  <div>
    <strong>Zuletzt geändert von:</strong>{' '}
    {header.updator
      ? `${header.updator.firstName} ${header.updator.lastName}`
      : 'Unbekannt'}{' '}
    am {formatDateTime(header.updatedAt)}
  </div>
</div>


      {/* Info-Header */}
<div
  style={{
    fontSize: '120%',
    lineHeight: 1.4,
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,                 // ⬅️ einheitlicher Zeilenabstand
  }}
>
  <div>
    <strong>Maschine:</strong> {header.machine ? header.machine.name : '—'} &nbsp;|&nbsp;
    <strong>Seriennummer:</strong> {header.machine?.serialNumber || '—'} &nbsp;|&nbsp;
    <strong>Baujahr:</strong> {header.machine?.yearOfManufacture || '—'}
  </div>
  <div>
    <strong>Hersteller:</strong> {header.machine?.manufacturer || '—'} &nbsp;|&nbsp;
    <strong>Typ:</strong> {header.machine?.manufacturerType || '—'}
  </div>
  <div>
    <strong>Zertifikat:</strong> {header.certificateNumber || '—'} &nbsp;|&nbsp;
    <strong>Prüfungsnorm:</strong> {header.standardCode || '—'}
  </div>
</div>
<strong></strong>{' '}
<span
  style={{
    padding: '6px 14px',          // ⬅️ größerer Innenabstand
    display: 'inline-block',
    borderRadius: 20,             // ⬅️ stärker abgerundet
    fontSize: '1.1em',            // ⬅️ etwas größere Schrift
    fontWeight: '600',            // ⬅️ fetterer Text
    color: '#fff',
    background:
      header.result === 'passed'
        ? '#28a745'
        : header.result === 'failed'
        ? '#dc3545'
        : '#6c757d',
    minWidth: 140,                // optional: gleiche Breite für alle Zustände
    textAlign: 'center',
  }}
>
  {header.result === 'passed'
    ? 'bestanden'
    : header.result === 'failed'
    ? 'nicht bestanden'
    : 'offen'}
</span>
      {/* Kopf-Form */}
      <form style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignSelf: 'flex-start' }}>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Prüfdatum</label>
            <input
              type="date"
              value={header.inspectionDate || ''}
              onChange={(e) => setHeader({ ...header, inspectionDate: e.target.value })}
              style={{ width: '100%', minHeight: 34 }}
            />
          </div>

          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Betriebsstunden</label>
            <input
              type="number"
              step="0.1"
              value={header.usageHours ?? ''}
              onChange={(e) => setHeader({ ...header, usageHours: e.target.value === '' ? null : Number(e.target.value) })}
              style={{ width: '100%', minHeight: 34 }}
            />
          </div>

          <div style={{ flex: '1 1 120px', minWidth: 120, alignSelf: 'flex-start' }}>
            <label>Prüfer</label>
            <Select
              isClearable
              placeholder="Prüfer wählen…"
              options={inspectorOptions}
              value={selectedInspector}
              onChange={(opt) => setHeader(h => ({ ...h, inspectorId: opt?.value ?? null }))}
              styles={{
                control: (base) => ({ ...base, minHeight: 34, width: '100%' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
            />
          </div>

          <div style={{ flex: '1 1 120px', minWidth: 120, alignSelf: 'flex-start', marginLeft:'-15px', marginRight:'10px', marginTop:'7px' }}>
            <label></label>
            <select
              value={header.uvvType || 'main'}
              onChange={(e) => setHeader({ ...header, uvvType: e.target.value })}
              style={{ width: '100%', minHeight: 34 }}
            >
              <option value="main">Hauptprüfung</option>
              <option value="retest">Nachprüfung</option>
            </select>
          </div>

         {/* ===== Kunde / Besteller / Einsatzort ===== */}
<div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginTop: 8 }}>
  {/* Kunde */}
  <div style={{ flex: '1 1 300px', minWidth: 300 }}>
    <label style={{ display: 'inline-block', marginBottom: 4 }}>Kunde *</label>
    <Select
      isClearable
      placeholder="Kunde wählen…"
      options={customerOptions}
      value={customerOptions.find(o => String(o.value) === String(header.customerId)) || null}
      onChange={(opt) => setHeader(h => ({ ...h, customerId: opt ? Number(opt.value) : null, contactId: null }))}
      styles={{
        control: (base) => ({ ...base, minHeight: 34, width: '100%' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
      }}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
    />
  </div>

  {/* Besteller */}
  <div style={{ flex: '1 1 200px', minWidth: 200 }}>
    <label style={{ display: 'inline-block', marginBottom: 4 }}>Besteller</label>
    <Select
      isClearable
      isDisabled={!header?.customerId}
      placeholder={!header?.customerId ? 'Bitte erst Kunde wählen' : 'Besteller wählen…'}
      options={contactOptions}
      value={selectedContact}
      onChange={(opt) => {
        setSelectedContact(opt || null);
        setHeader(h => ({ ...h, contactId: opt ? Number(opt.value) : null }));
      }}
      styles={{
        control: (base) => ({ ...base, minHeight: 34, width: '100%' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
      }}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
    />
  </div>

  {/* Einsatzort */}
  <div style={{ flex: '2 1 320px', minWidth: 320, marginBottom:'-15px'  }}>
    <label style={{ display: 'inline-block', marginBottom: 4 }}>Einsatzort</label>
    <input
      type="text"
      placeholder="z. B. Musterstraße 1, 12345 Musterstadt"
      value={header.operationLocation || ''}
      onChange={(e) => setHeader(h => ({ ...h, operationLocation: e.target.value }))}
      style={{ width: '100%', minHeight: 34 }}
    />
  </div>

  {/* ✅ Checkbox: An LW senden — RECHTS neben Einsatzort */}
  <div style={{ flex: '0 0 auto', minWidth: 180, display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2, marginBottom: -17 }}>
    <input
      id="inspectionToLW"
      type="checkbox"
      checked={!!header.inspectionToLW}
      onChange={(e) => {
        setHeader(h => ({ ...h, inspectionToLW: e.target.checked }));
        setHeaderDirty(true);
      }}
      style={{ width: 40, height: 40 }}
    />
    <label htmlFor="inspectionToLW" style={{ userSelect: 'none', marginBottom: 6 }}>
      LW-Maschinenakte
    </label>
  </div>
</div>
{/* ===== /Kunde / Besteller / Einsatzort ===== */}

          {/* ===== /Kunde / Besteller / Einsatzort ===== */}

          {/* Aktionen rechts */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" onClick={saveHeader} disabled={savingHead}>
              {savingHead ? 'Speichere…' : 'Kopf speichern'}
            </button>

            <button type="button" onClick={() => navigate('/uvvInspectionList')}>
              Liste
            </button>
          </div>

          {/* Breite Felder */}
          <div style={{ flex: '1 1 100%', minWidth: 320 }}>
            <label>Mängel (gesamt)</label>
            <textarea
              rows={4}
              value={header.defects || ''}
              onChange={(e) => setHeader({ ...header, defects: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1 1 100%', minWidth: 320 }}>
            <label>Berichtsnotiz</label>
            <textarea
              rows={4}
              value={header.reportNote || ''}
              onChange={(e) => setHeader({ ...header, reportNote: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1 1 100%', minWidth: 320 }}>
            <label>Notiz</label>
            <textarea
              rows={4}
              value={header.note || ''}
              onChange={(e) => setHeader({ ...header, note: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </form>

      {/* Filter über Tabelle */}
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
        <label>Filter:</label>
        <select value={only} onChange={(e) => setOnly(e.target.value)} style={{ minHeight: 34 }}>
          <option value="all">Alle</option>
          <option value="open">Nur offen</option>
          <option value="notok">Nur nicht i.O.</option>
        </select>

        <input
          type="text"
          placeholder="Suche Kap./Nr./Beschreibung/Bemerkung…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ minHeight: 34, marginTop:'10px' }}
        />

        <label>Ergebnis:</label>
        <select
          value={header.result || 'pending'}
          onChange={(e) => { setHeader({ ...header, result: e.target.value }); setHeaderDirty(true); }}
          style={{ minHeight: 34 }}
          title="Prüfungsergebnis manuell festlegen"
        >
          <option value="pending">offen</option>
          <option value="passed">bestanden</option>
          <option value="failed">nicht bestanden</option>
        </select>

        <button
          onClick={async () => { await saveHeader(); setHeaderDirty(false); }}
          disabled={!headerDirty || savingHead}
          title={headerDirty ? 'Ergebnis speichern' : 'Keine Änderung am Ergebnis'}
        >
          {savingHead ? 'Speichere…' : 'Ergebnis übernehmen'}
        </button>

        <button
          onClick={saveItems}
          disabled={!itemsDirty || savingItems}
          title={itemsDirty ? 'Änderungen speichern' : 'Keine Änderungen'}
        >
          {savingItems ? 'Speichere…' : 'Positionen speichern'}
        </button>
      </div>

      {/* Tabelle */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fffefeff' }}>
              <th style={{ textAlign: 'left',  padding: 8, width: 70  }}>Nr.</th>
              <th style={{ textAlign: 'left',  padding: 8              }}>Beschreibung</th>
              <th style={{ textAlign: 'center',padding: 8, width: 120 }}>Vollständigkeit</th>
              <th style={{ textAlign: 'center',padding: 8, width: 140 }}>Zustand i.O.</th>
              <th style={{ textAlign: 'left',  padding: 8, width: 280 }}>Bemerkungen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 12, textAlign: 'center' }}>
                  Keine Positionen
                </td>
              </tr>
            )}

            {(() => {
              const groups = new Map();
              for (const it of filtered) {
                const key = it.category || '—';
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(it);
              }

              return Array.from(groups.entries()).map(([cat, arr]) => (
                <React.Fragment key={`cat-${cat || 'none'}`}>
                  <tr style={{ background: '#9b9b9bff', borderTop: '1px solid #ddd' }}>
                    <td colSpan={5} style={{
                      padding: 8,
                      fontWeight: 800,
                      fontSize: '140%',
                      textAlign: 'center',
                      color: '#ffffffff',
                    }}>
                      Kapitel: {cat}
                    </td>
                  </tr>

                  {arr.map((it) => (
                    <tr key={it.id} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: 8 }}>{it.sectionNumber}</td>
                      <td style={{ padding: 8 }}>{it.description}</td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <TriState
                          value={it.completeness}
                          onChange={(v) => updateItemField(it.id, 'completeness', v)}
                          labels={['—', '+', '–']}
                        />
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <TriState
                          value={it.conditionOk}
                          onChange={(v) => updateItemField(it.id, 'conditionOk', v)}
                          labels={['—', 'Ja', 'Nein']}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <input
                          type="text"
                          value={it.remarks || ''}
                          onChange={(e) => updateItemField(it.id, 'remarks', e.target.value)}
                          placeholder="z. B. ‚leichter Ölfilm am Zylinder‘"
                          style={{ width: '100%', minHeight: 32 }}
                        />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ));
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UVVInspectionForm;
